import { addEntity, removeEntity } from 'bitecs';
import { Animal, Plant, Position, Tile, animalQuery, plantQuery, tileQuery } from '../world.js';
import { TILE_SIZE, CHUNK_SIZE, MAP_WIDTH, MAP_HEIGHT, CURRENT_MODE } from '../config.js';
import { ANIMAL_TYPES, PLANT_TYPES } from '../assets/entityTypes.js';

/**
 * Balanced spawning system that maintains ecosystem equilibrium
 */
export class BalancedSpawner {
    constructor() {
        this.spawnTargets = this.initializeSpawnTargets();
        this.currentPopulations = new Map();
        this.lastCensus = 0;
        this.censusInterval = 10000; // Census every 10 seconds
        this.spawnCooldown = new Map();
        this.maxSpawnRate = 5; // Max entities spawned per second
    }
    
    /**
     * Initialize balanced spawn targets based on map size
     */
    initializeSpawnTargets() {
        const mapArea = MAP_WIDTH * MAP_HEIGHT;
        const areaMultiplier = mapArea / 1000; // Per 1000 tiles
        
        return {
            animals: {
                RABBIT: Math.floor(8 * areaMultiplier),      // Primary prey
                CHICKEN: Math.floor(6 * areaMultiplier),     // Secondary prey
                WOLF: Math.floor(1 * areaMultiplier),        // Apex predator
                HAWK: Math.floor(0.5 * areaMultiplier),      // Aerial predator
                DEER: Math.floor(4 * areaMultiplier)         // Large herbivore
            },
            plants: {
                TREE: Math.floor(15 * areaMultiplier),       // Primary vegetation
                FLOWER: Math.floor(20 * areaMultiplier),     // Pollinator food
                MUSHROOM: Math.floor(8 * areaMultiplier),    // Decomposer food
                HERB: Math.floor(12 * areaMultiplier)        // Medicinal/food
            }
        };
    }
    
    /**
     * Conduct population census
     */
    conductCensus(world) {
        const census = {
            animals: {},
            plants: {},
            totalAnimals: 0,
            totalPlants: 0
        };
        
        try {
            // Count animals
            const animals = animalQuery(world);
            for (const animal of animals) {
                // Use Animal.type instead of Animal.species since that's what's defined in world.js
                const animalTypeValue = Animal.type[animal];
                let species = 'UNKNOWN';
                
                // Map animal type values to species names
                if (animalTypeValue === 1) species = 'RABBIT';
                else if (animalTypeValue === 2) species = 'CHICKEN';
                else if (animalTypeValue === 3) species = 'WOLF';
                else if (animalTypeValue === 4) species = 'HAWK';
                else if (animalTypeValue === 5) species = 'DEER';
                else if (animalTypeValue === 6) species = 'GOAT';
                
                census.animals[species] = (census.animals[species] || 0) + 1;
                census.totalAnimals++;
            }
            
            // Count plants
            const plants = plantQuery(world);
            for (const plant of plants) {
                const plantType = Plant.type[plant];
                const typeName = this.getPlantTypeName(plantType);
                census.plants[typeName] = (census.plants[typeName] || 0) + 1;
                census.totalPlants++;
            }
        } catch (error) {
            console.error('[BalancedSpawner] Error during census:', error);
            return this.currentPopulations || { animals: {}, plants: {}, totalAnimals: 0, totalPlants: 0 };
        }
        
        this.currentPopulations = census;
        this.lastCensus = Date.now();
        
        return census;
    }
    
    /**
     * Get plant type name from ID
     */
    getPlantTypeName(typeId) {
        for (const [name, type] of Object.entries(PLANT_TYPES)) {
            if (type.id === typeId) return name;
        }
        return 'UNKNOWN';
    }
    
    /**
     * Balance populations through spawning and culling
     */
    balancePopulations(world) {
        try {
            const census = this.conductCensus(world);
            const now = Date.now();
            
            // Balance animal populations
            this.balanceAnimalPopulations(world, census.animals, now);
            
            // Balance plant populations
            this.balancePlantPopulations(world, census.plants, now);
        } catch (error) {
            console.error('[BalancedSpawner] Error balancing populations:', error);
        }
    }
    
    /**
     * Balance animal populations
     */
    balanceAnimalPopulations(world, currentAnimals, now) {
        for (const [species, targetCount] of Object.entries(this.spawnTargets.animals)) {
            const current = currentAnimals[species] || 0;
            const difference = targetCount - current;
            
            if (difference > 0 && this.canSpawn(species, now)) {
                // Need to spawn more
                const toSpawn = Math.min(difference, this.maxSpawnRate);
                this.spawnAnimals(world, species, toSpawn);
            } else if (difference < 0 && current > targetCount * 1.5) {
                // Overpopulation - natural culling
                const toCull = Math.min(Math.abs(difference), Math.floor(current * 0.1));
                this.cullAnimals(world, species, toCull);
            }
        }
    }
    
    /**
     * Balance plant populations
     */
    balancePlantPopulations(world, currentPlants, now) {
        for (const [plantType, targetCount] of Object.entries(this.spawnTargets.plants)) {
            const current = currentPlants[plantType] || 0;
            const difference = targetCount - current;
            
            if (difference > 0 && this.canSpawn(plantType, now)) {
                // Need to spawn more
                const toSpawn = Math.min(difference, this.maxSpawnRate * 2);
                this.spawnPlants(world, plantType, toSpawn);
            }
        }
    }
    
    /**
     * Check if species can spawn (cooldown)
     */
    canSpawn(species, now) {
        const lastSpawn = this.spawnCooldown.get(species) || 0;
        return now - lastSpawn > 2000; // 2 second cooldown
    }
    
    /**
     * Spawn animals of specific species
     */
    spawnAnimals(world, species, count) {
        const tiles = tileQuery(world);
        const landTiles = tiles.filter(tile => Tile.type[tile] === 1); // Land tiles
        
        for (let i = 0; i < count && landTiles.length > 0; i++) {
            const tile = landTiles[Math.floor(Math.random() * landTiles.length)];
            const x = Tile.x[tile] * TILE_SIZE + Math.random() * TILE_SIZE;
            const y = Tile.y[tile] * TILE_SIZE + Math.random() * TILE_SIZE;
            
            if (this.isValidSpawnLocation(x, y, species)) {
                this.createAnimal(world, species, x, y);
            }
        }
        
        this.spawnCooldown.set(species, Date.now());
    }
    
    /**
     * Spawn plants of specific type
     */
    spawnPlants(world, plantType, count) {
        const tiles = tileQuery(world);
        const landTiles = tiles.filter(tile => Tile.type[tile] === 1); // Land tiles
        
        for (let i = 0; i < count && landTiles.length > 0; i++) {
            const tile = landTiles[Math.floor(Math.random() * landTiles.length)];
            const x = Tile.x[tile] * TILE_SIZE + Math.random() * TILE_SIZE;
            const y = Tile.y[tile] * TILE_SIZE + Math.random() * TILE_SIZE;
            
            if (this.isValidPlantLocation(x, y, plantType)) {
                this.createPlant(world, plantType, x, y);
            }
        }
        
        this.spawnCooldown.set(plantType, Date.now());
    }
    
    /**
     * Check if location is valid for animal spawn
     */
    isValidSpawnLocation(x, y, species) {
        // Check biome compatibility
        const tileX = Math.floor(x / TILE_SIZE);
        const tileY = Math.floor(y / TILE_SIZE);
        
        // Simple validation - can be expanded with biome checks
        return x >= 0 && x < MAP_WIDTH * TILE_SIZE && 
               y >= 0 && y < MAP_HEIGHT * TILE_SIZE;
    }
    
    /**
     * Check if location is valid for plant spawn
     */
    isValidPlantLocation(x, y, plantType) {
        const tileX = Math.floor(x / TILE_SIZE);
        const tileY = Math.floor(y / TILE_SIZE);
        
        return x >= 0 && x < MAP_WIDTH * TILE_SIZE && 
               y >= 0 && y < MAP_HEIGHT * TILE_SIZE;
    }
    
    /**
     * Create animal entity
     */
    createAnimal(world, species, x, y) {
        const animal = addEntity(world);
        
        // Map species name to type value
        let typeValue = 0;
        if (species === 'RABBIT') typeValue = 1;
        else if (species === 'CHICKEN') typeValue = 2;
        else if (species === 'WOLF') typeValue = 3;
        else if (species === 'HAWK') typeValue = 4;
        else if (species === 'DEER') typeValue = 5;
        else if (species === 'GOAT') typeValue = 6;
        
        Animal.type[animal] = typeValue;
        Animal.x[animal] = x;
        Animal.y[animal] = y;
        Animal.age[animal] = 0;
        Animal.energy[animal] = 80;
        Animal.maxEnergy[animal] = 100;
        Animal.isMoving[animal] = 0;
        Animal.isFleeing[animal] = 0;
        
        Position.x[animal] = x;
        Position.y[animal] = y;
        
        return animal;
    }
    
    /**
     * Create plant entity
     */
    createPlant(world, plantType, x, y) {
        const plant = addEntity(world);
        
        Plant.type[plant] = PLANT_TYPES[plantType]?.id || 1;
        Plant.x[plant] = Math.floor(x / TILE_SIZE);
        Plant.y[plant] = Math.floor(y / TILE_SIZE);
        Plant.growth[plant] = 0.1;
        Plant.age[plant] = 0;
        Plant.energy[plant] = 30;
        Plant.harvested[plant] = 0;
        
        Position.x[plant] = x;
        Position.y[plant] = y;
        
        return plant;
    }
    
    /**
     * Cull animals (natural death)
     */
    cullAnimals(world, species, count) {
        const animals = animalQuery(world);
        
        // Map species to type value for comparison
        let targetTypeValue = 0;
        if (species === 'RABBIT') targetTypeValue = 1;
        else if (species === 'CHICKEN') targetTypeValue = 2;
        else if (species === 'WOLF') targetTypeValue = 3;
        else if (species === 'HAWK') targetTypeValue = 4;
        else if (species === 'DEER') targetTypeValue = 5;
        else if (species === 'GOAT') targetTypeValue = 6;
        
        const targets = animals.filter(animal => Animal.type[animal] === targetTypeValue);
        
        for (let i = 0; i < count && i < targets.length; i++) {
            // Prioritize old, weak, or sick animals
            const sorted = targets.sort((a, b) => {
                const scoreA = Animal.age[a] + (100 - Animal.energy[a]);
                const scoreB = Animal.age[b] + (100 - Animal.energy[b]);
                return scoreB - scoreA; // Highest score first (oldest/weakest)
            });
            
            if (sorted.length > 0) {
                removeEntity(world, sorted[0]);
            }
        }
    }
    
    /**
     * Get ecosystem balance metrics
     */
    getBalanceMetrics() {
        return {
            targets: this.spawnTargets,
            current: this.currentPopulations,
            balance: this.calculateBalanceScore(),
            lastCensus: this.lastCensus
        };
    }
    
    /**
     * Calculate ecosystem balance score (0-100)
     */
    calculateBalanceScore() {
        let totalScore = 0;
        let categories = 0;
        
        // Check animal balance
        for (const [species, target] of Object.entries(this.spawnTargets.animals)) {
            const current = this.currentPopulations.animals[species] || 0;
            const ratio = current / target;
            const score = Math.max(0, 100 - Math.abs(ratio - 1) * 100);
            totalScore += score;
            categories++;
        }
        
        // Check plant balance
        for (const [plantType, target] of Object.entries(this.spawnTargets.plants)) {
            const current = this.currentPopulations.plants[plantType] || 0;
            const ratio = current / target;
            const score = Math.max(0, 100 - Math.abs(ratio - 1) * 100);
            totalScore += score;
            categories++;
        }
        
        return categories > 0 ? totalScore / categories : 0;
    }
    
    /**
     * Update spawn targets based on map size changes
     */
    updateSpawnTargets() {
        this.spawnTargets = this.initializeSpawnTargets();
    }
}

// Export singleton instance
export const balancedSpawner = new BalancedSpawner();
